/**
 * useServiceLogs — fetches K8s pod logs for a service within an application.
 *
 * Caching strategy:
 *  - On first fetch, all log entries are fetched and cached.
 *  - On subsequent polls, only the last `sinceSeconds` of logs are fetched,
 *    and new entries are merged into the existing cache by (podName, containerName)
 *    deduplication. Log lines are appended only if they differ from what's cached.
 *  - When the alias or serviceName changes, the cache is cleared and refetched.
 *
 * Options:
 *  - autoFetch: fetch on mount (default true)
 *  - pollInterval: polling interval in ms (default 5000)
 */

import { useState, useEffect, useCallback, useRef } from "react";

import applicationApi from "../module/api/applicationApi";
import type {
  ServiceLogEntry,
  ServiceLogsResult,
} from "../module/api/IApplicationApi";

export interface UseServiceLogsResult {
  entries: ServiceLogEntry[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/** Dedupe key for a log entry: podName + containerName uniquely identifies a container. */
function entryKey(entry: ServiceLogEntry): string {
  return `${entry.podName}::${entry.containerName}`;
}

export function useServiceLogs(
  alias: string | undefined,
  serviceName: string | undefined,
  options?: { autoFetch?: boolean; pollInterval?: number },
): UseServiceLogsResult {
  const [entries, setEntries] = useState<ServiceLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  // Cache for incremental merges — invalidated on alias/serviceName change
  const cacheRef = useRef<Map<string, ServiceLogEntry>>(new Map());
  const cacheKeyRef = useRef(`${alias ?? ""}:${serviceName ?? ""}`);
  const isInitialFetchRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Invalidate cache when alias or serviceName changes
  useEffect(() => {
    const newKey = `${alias ?? ""}:${serviceName ?? ""}`;
    if (newKey !== cacheKeyRef.current) {
      cacheKeyRef.current = newKey;
      cacheRef.current.clear();
      isInitialFetchRef.current = true;
    }
  }, [alias, serviceName]);

  const fetchLogs = useCallback(async () => {
    if (!alias || !serviceName) return;
    setLoading(true);
    setError(null);
    try {
      // On initial fetch, get last 5 minutes of logs.
      // On subsequent polls, only get last 60 seconds (incremental).
      const sinceSeconds = isInitialFetchRef.current ? 300 : 60;

      const result: ServiceLogsResult = await applicationApi.getServiceLogs(
        alias,
        serviceName,
        { tailLines: 200, sinceSeconds },
      );

      if (mountedRef.current) {
        // Merge new entries into cache
        const newCache = new Map(cacheRef.current);

        for (const entry of result.logs) {
          const key = entryKey(entry);
          const cached = newCache.get(key);

          if (!cached) {
            // New pod/container — add to cache
            newCache.set(key, entry);
          } else if (entry.logs !== cached.logs) {
            // Existing pod with new log lines — append only the diff.
            // If the new logs start with the same content as cached, just replace.
            // Otherwise, append new content after the last known line.
            if (entry.logs.startsWith(cached.logs)) {
              // New response is a superset of cached — use it directly
              newCache.set(key, entry);
            } else {
              // Logs have rotated or changed — append new lines that aren't in cache
              const cachedLines = cached.logs.split("\n");
              const newLines = entry.logs.split("\n");
              // Find the overlap point: look for the last cached line in the new output
              const lastCachedLine = cachedLines[cachedLines.length - 1] || "";
              const overlapIdx = newLines.indexOf(lastCachedLine);
              if (overlapIdx >= 0 && overlapIdx < newLines.length - 1) {
                // Append lines after the overlap point
                const appendedLines = newLines.slice(overlapIdx + 1);
                const mergedLogs =
                  cached.logs + "\n" + appendedLines.join("\n");
                newCache.set(key, { ...entry, logs: mergedLogs });
              } else {
                // Can't find overlap — replace entirely (logs may have rotated)
                newCache.set(key, entry);
              }
            }
          }
          // If logs are identical, keep the cached entry (no re-render)
        }

        cacheRef.current = newCache;
        isInitialFetchRef.current = false;
        setEntries(Array.from(newCache.values()));

        if (result.logs.length === 0 && !result.namespace) {
          setError("No pods found for this service");
        }
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(
          err instanceof Error ? err.message : "Failed to fetch service logs",
        );
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [alias, serviceName]);

  useEffect(() => {
    if (options?.autoFetch !== false) {
      void fetchLogs();
    }
  }, [fetchLogs, options?.autoFetch]);

  // Poll on interval while component is active
  useEffect(() => {
    if (!options?.pollInterval) return;
    const id = setInterval(() => {
      if (mountedRef.current && options?.pollInterval) {
        void fetchLogs();
      }
    }, options?.pollInterval ?? 5000);
    return () => clearInterval(id);
  }, [fetchLogs, options?.pollInterval]);

  return { entries, loading, error, refetch: fetchLogs };
}
